/**
 * Helpers for comparing DB vs MLB data and assigning diff state (only_db | only_mlb | diff_value).
 * Pictures are not compared; product pictures are the source of truth (MLB URLs change after upload).
 */

/**
 * Compare two values (string/number) for equality.
 */
function sameValue(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

/**
 * Scalar diff: returns only_db (only we have), only_mlb (only MLB has), diff_value (both but different), or null (match).
 */
export function scalarDiff(dbValue, mlbValue) {
  const dbPresent = dbValue != null && String(dbValue).trim() !== '';
  const mlbPresent = mlbValue != null && String(mlbValue).trim() !== '';
  if (!dbPresent && !mlbPresent) return null;
  if (dbPresent && !mlbPresent) return 'only_db';
  if (!dbPresent && mlbPresent) return 'only_mlb';
  return sameValue(dbValue, mlbValue) ? null : 'diff_value';
}

/**
 * Get warranty value from MLB sale_terms (WARRANTY_TYPE).
 */
export function getMlbWarranty(saleTerms) {
  if (!Array.isArray(saleTerms)) return null;
  const t = saleTerms.find((s) => s && (s.id === 'WARRANTY_TYPE' || s.name === 'Tipo de garantia'));
  return t && (t.value_name != null || t.value_id != null) ? (t.value_name || t.value_id || null) : null;
}

/**
 * Merge attributes (product additional details with mlb_attribute_id vs item.attributes) with diff state.
 * @param {Array<{ mlb_attribute_id?: string, detail_value?: string }>} productAttrs
 * @param {Array<{ id?: string, value_name?: string }>} mlbAttrs
 * @returns {Array<{ id: string, name?: string, value: string, diffState: 'only_db' | 'only_mlb' | 'diff_value' | null }>}
 */
export function mergeAttributesWithDiff(productAttrs, mlbAttrs) {
  const mlbMap = new Map();
  (mlbAttrs || []).forEach((a) => {
    const id = (a && a.id) ? String(a.id) : '';
    if (id) mlbMap.set(id, { value_name: a.value_name, name: a.name });
  });
  const result = [];
  const seen = new Set();
  (productAttrs || []).filter((a) => a && a.mlb_attribute_id).forEach((a) => {
    const id = String(a.mlb_attribute_id).trim();
    if (!id) return;
    seen.add(id);
    const mlb = mlbMap.get(id);
    const dbVal = a.detail_value != null ? String(a.detail_value).trim() : '';
    const mlbVal = mlb && mlb.value_name != null ? String(mlb.value_name).trim() : '';
    let diffState = scalarDiff(dbVal || null, mlbVal || null);
    if (diffState === null && !mlb) diffState = 'only_db';
    else if (diffState === null && mlb) diffState = null;
    result.push({
      id,
      name: mlb && mlb.name ? mlb.name : id,
      value: mlbVal || dbVal || '—',
      diffState: diffState || null
    });
  });
  mlbMap.forEach((mlb, id) => {
    if (seen.has(id)) return;
    result.push({
      id,
      name: mlb.name || id,
      value: mlb.value_name != null ? String(mlb.value_name).trim() : '—',
      diffState: 'only_mlb'
    });
  });
  return result;
}
